import type {
  InvoiceRow,
  InvoiceListFilters,
  Locale,
  InvoiceStatus,
} from "./types";

export function fmtMoney(minor: number, locale: Locale): string {
  const value = minor / 100;
  return `₺${value.toLocaleString(locale === "tr" ? "tr-TR" : "en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function fmtDate(iso: string | null, locale: Locale): string {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat(locale === "tr" ? "tr-TR" : "en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function fmtDateTime(iso: string, locale: Locale): string {
  try {
    return new Intl.DateTimeFormat(locale === "tr" ? "tr-TR" : "en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function fmtTaxNumber(taxNumber: string | null): string {
  if (!taxNumber) return "—";
  const digits = taxNumber.replace(/\D/g, "");
  if (digits.length === 11) return `TCKN: ${digits}`;
  if (digits.length === 10) return `VKN: ${digits}`;
  return taxNumber;
}

export function isOverdue(row: InvoiceRow, today: Date = new Date()): boolean {
  if (row.status !== "issued" && row.status !== "sent") return false;
  if (!row.dueAt) return false;
  const t = today.toISOString().slice(0, 10);
  return row.dueAt.slice(0, 10) < t;
}

export function daysUntil(iso: string | null, today: Date = new Date()): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  const ms = d.getTime() - today.getTime();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export function relativeOverdueDays(iso: string | null, today: Date = new Date()): number {
  if (!iso) return 0;
  const d = new Date(iso);
  const ms = today.getTime() - d.getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
}

export function emptyFilters(): InvoiceListFilters {
  return {
    search: "",
    status: [],
    type: [],
    dateRange: null,
    customerId: null,
    sort: { field: "issuedAt", dir: "desc" },
    page: 1,
    pageSize: 25,
  };
}

export function hasActiveFilters(f: InvoiceListFilters): boolean {
  return (
    f.search.trim().length > 0 ||
    f.status.length > 0 ||
    f.type.length > 0 ||
    f.dateRange !== null ||
    f.customerId !== null
  );
}

export function statusColor(s: InvoiceStatus): {
  bg: string;
  fg: string;
  border: string;
} {
  switch (s) {
    case "paid":
      return {
        bg: "bg-biz-success-subtle",
        fg: "text-biz-success",
        border: "border-biz-success-border",
      };
    case "issued":
    case "sent":
      return {
        bg: "bg-biz-primary-subtle",
        fg: "text-biz-primary-subtle-fg",
        border: "border-biz-primary/30",
      };
    case "draft":
      return {
        bg: "bg-biz-muted",
        fg: "text-biz-foreground-muted",
        border: "border-biz-border-strong border-dashed",
      };
    case "voided":
      return {
        bg: "bg-biz-muted",
        fg: "text-biz-foreground-subtle",
        border: "border-biz-border",
      };
    case "failed":
      return {
        bg: "bg-biz-danger-subtle",
        fg: "text-biz-danger",
        border: "border-biz-danger-border",
      };
  }
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s);
}

export function isValidPhone(s: string): boolean {
  return /^\+?\d[\d\s\-()]{7,}$/.test(s);
}
